import { useConfigStore } from '@/stores/ConfigStore'
import { useConnectionStore } from '@/stores/ConnectionStore'
import { useCardStore } from '@/stores/CardStore'
import { isValidSku } from '@/helpers/sku'
import { 
  REQUIRED_FIELDS_FOR_LIST_TO_COLLX, 
  CONDITION, 
  TCG_CONDITION, 
  VIEW_GRADER_WEBSITE_LOOKUP_LINKS,
  EBAY_TYPES_BY_CATEGORIES,
} from '@/constants'

export const getDefaultSelectedCardId = () => {
  const cardStore = useCardStore()
  
  return cardStore.batchCardsBasedOnStatus.draftBatchCards.length ? cardStore.batchCardsBasedOnStatus.draftBatchCards[0].id : cardStore.batchCardsBasedOnStatus.readyToListBatchCards.length ? cardStore.batchCardsBasedOnStatus.readyToListBatchCards[0].id : null
}

export const getNextCardId = () => {
  const cardStore = useCardStore()
  const { draftBatchCards, readyToListBatchCards } = cardStore.batchCardsBasedOnStatus
  const currentId = cardStore.selectedCardId

  const currentIndex = draftBatchCards.findIndex(card => card.id === currentId)

  // The current card is still a draft — e.g. "Mark as Ready to List", which
  // fires an async status update but navigates synchronously, so the card
  // hasn't left the draft list yet. Advance to the *next* draft after it.
  if (currentIndex !== -1) {
    const nextDraft = draftBatchCards[currentIndex + 1]

    if (nextDraft) return nextDraft.id

    // Last in the draft list, but earlier drafts still remain (they'll stay
    // draft after this card flips). Wrap to the first of those remaining drafts
    // — skip `draftBatchCards[0]` when it *is* the current card, otherwise we'd
    // land back on the same card after it becomes Ready to List.
    const remainingDraft = draftBatchCards.find(card => card.id !== currentId)

    if (remainingDraft) return remainingDraft.id

    // No drafts left after this one → move into Ready to List. Fall back to
    // staying on the current card when nothing else is ready yet.
    return readyToListBatchCards[0]?.id || currentId
  }

  // The current card is no longer a draft (e.g. it was just deleted, or already
  // ready): fall back to the first remaining draft, then the first ready card.
  return draftBatchCards[0]?.id || readyToListBatchCards[0]?.id
}

export const omitQueryFilters = (fieldNames) => {
  const cardStore = useCardStore()
  
  return Object.keys(cardStore.itemFilters).reduce((acc, item) => {
    if (!fieldNames.includes(item)) {
      acc[item] = cardStore.itemFilters[item]
    }

    return acc
  }, {})
}

export const toggleAllRows = ({ params }) => {
  const cardStore = useCardStore()

  const haveUnselectedItems = !!cardStore.cards.find(element => element.select === false)

  if (haveUnselectedItems) {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })
    cardStore.cards = cardStore.cards.map((element, index) => {
      if (!element.select) {
        element.select = true
        if (cardStore.selectedRanges) {
          cardStore.selectedRanges.push({ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          })

          cardStore.selectedCards[element.id] = element
        } else {
          cardStore.selectedRanges = [{ 
            startIndex: index, 
            endIndex: index, 
            id: element.id,
            statusDisabled: element.status === 'draft' && element.has_errors,
            graded: element.graded 
          }] 

          cardStore.selectedCards[element.id] = element    
        }
      }
      return element
    })
  } else {
    params.api.refreshCells({
      force: true,
      suppressFlash: true,
    })

    cardStore.cards.forEach(element => {
      if (element.select) {
        element.select = false
        cardStore.selectedRanges = null
        cardStore.selectedCards = {}
        cardStore.isBulkEditing = false
      }
    })
  }
}

export const handleOnRowClicked = (params, setFocus = false) => {
  const cardStore = useCardStore()
  
  cardStore.pinnedCardData.isMatchOnly = false
  cardStore.selectedCardId = params.data.id
  cardStore.selectedCardData = cardStore.cards.find(item => item.id === Number(cardStore.selectedCardId)) || {}

  if (params.data.select) {
    params.node.setSelected(true)
  }

  if (setFocus) {
    const selectedCell = document.querySelectorAll('.ag-cell-range-selected')

    if (selectedCell[0]) {
      selectedCell[0].classList.remove('ag-cell-range-selected')
      selectedCell[0].classList.remove('ag-cell-range-selected-1')
    }
    params.api.setFocusedCell(params.rowIndex, 'database_match')
  }
}

export const sortSubsetSuggestions = (a, b) => {
  const subsetA = a.set.subset !== null ? a.set.subset : a.set.name
  const subsetB = b.set.subset !== null ? b.set.subset : b.set.name

  if (subsetA < subsetB) return -1
  if (subsetA > subsetB) return 1
  return 0
}

export const getCardFiltersString = (card) => {
  const { graded, grader, grade_number, serial_number } = card

  let filters = `&_sop=15&Graded=${graded ? 'Yes' : 'No'}`

  if (grader) {
    filters += `&Grader=${grader}`
  }

  if (grade_number) {
    filters += `&Grade=${grade_number}`
  }

  if (serial_number) {
    filters += `&Features=Serial Numbered`
  }

  return filters
}

export const getCardNameForPricing = (card) => {
  let name = ''
  const { card_number, set, player, subset, variation, grader, grade_number, serial_number } = card

  if (set) {
    name = `${name} ${set}`
  } 

  if (card_number) {
    name = `${name} #${card_number}`
  }

  if (player) {
    name = `${name} ${player}`
  } 

  if (subset) {
    name = `${name} ${subset}`
  } 

  if (variation) {
    name = `${name} ${variation}`
  } 

  if (grader) {
    name = `${name} ${grader}`
  } 

  if (grade_number) {
    name = `${name} ${grade_number}`
  } 

  if (serial_number) {
    name = `${name} ${serial_number}`
  }

  return encodeURIComponent(name)
}

const EBAY_AFFILIATE_PARAMS = '&mkcid=1&mkrid=711-53200-19255-0&siteid=0&campid=5338833009&customid=&toolid=10001&mkevt=1'
export const ebayRecentSalesUrl = (card) =>
  `https://www.ebay.com/sch/i.html?LH_Sold=1&_nkw=${getCardNameForPricing(card)}${EBAY_AFFILIATE_PARAMS}`
export const ebayActiveListingsUrl = (card, { filters = false, sort = false } = {}) =>
  `https://www.ebay.com/sch/i.html?_nkw=${getCardNameForPricing(card)}${EBAY_AFFILIATE_PARAMS}${filters ? getCardFiltersString(card) : ''}${sort ? '&_sop=15' : ''}`

const addDigitsToSku = (skuNumber, skuWidth) => {
  let skuString = String(skuNumber)
  while (skuString.length < skuWidth) {
    skuString = '0' + skuString
  }
  return skuString
}

// Preview of the next auto-generated SKU. The prefix is concatenated with no
// separator because that is what the API actually emits (`ABC` + `10000000376`
// → `ABC10000000376`); the space this used to insert made the preview both
// wrong and — since CDP-2444 — a display of an illegal SKU character.
export const preparePreviewSku = (skuNumber, skuWidth, skuPrefix) => {
  const skuString = addDigitsToSku(skuNumber, skuWidth)
  return skuPrefix ? `${skuPrefix}${skuString}` : skuString
}

export const getTitle = (card) => {
  const selectedCard = card

  if (selectedCard?.title) {
    return selectedCard.title
  }

  if (selectedCard?.template_title) {
    return selectedCard.template_title
  }

  return ''
}

export const getSuggestedTitle = (card) =>
  card?.llm
    ? [card.llm.cardNumber, card.llm.name, card.llm.set, card.llm.serialNumber].filter(Boolean).join(' ').trim()
    : ''

export const getTitleUsingSpecificFields = (card, connection) => {
	if (!card.card_number && !card.player && !card.team) {
 		return card[`${connection}_title`]
	}

	const combinedTitle = `${card.card_number ? `#${card.card_number}` : ''} ${card.player || ''} ${card.team || ''}`
	
	return combinedTitle 
}

export const isItNoMatchFound = (card) => {
  const selectedCard = card

  if (!selectedCard?.title && !selectedCard.player && !selectedCard.set && !selectedCard.card_number && !selectedCard.team && !selectedCard.year) {
    return true
  }

  return false
}

export const getErrorsForListToCollX = (card, onlyErrors = false) => {
  const { errors, suggested_edits: suggestedEdits, type } = card
  const errorsList = []

  REQUIRED_FIELDS_FOR_LIST_TO_COLLX.forEach((field) => {
    if (field === 'category') {
      if (type === 'sports') {
        errors?.sport && errorsList.push(errors.sport)
        !onlyErrors && suggestedEdits?.sport && errorsList.push(suggestedEdits.sport)
      } else {
        errors?.game && errorsList.push(errors.game)
        !onlyErrors && suggestedEdits?.game && errorsList.push(suggestedEdits.game)
      }
    } else {
      if (errors && (field !== 'start_price' || (errors[field] !== `Sale Price: On eBay, the Buy It Now price must be greater than or equal to US $0.99`))) {
        errors[field] && errorsList.push(errors[field])
        !onlyErrors && suggestedEdits[field] && errorsList.push(suggestedEdits[field])
      }
    }
  })

  const cardStore = useCardStore()
  const configStore = useConfigStore()

  const graders = configStore.getConfigField('graders') || []

  if (card.graded && !card.cert_no) {
    errorsList.push('Cert No: To list on CollX, a certificate number is required for graded cards')
  }

  if (card.graded && card.grader) {
    // check if grader from CDP grader list
    const grader = graders.find((item) => item.name === card.grader)

    if (!grader) {
      errorsList.push('Grader: To list on CollX, please select a Grader from available options')
    }

    // check if grade number from CDP grade numbers list
    if (grader && card.grade_number) {
      const gradeNumber = grader.grading_scale.find((item) => item === card.grade_number)

      if (!gradeNumber) {
        errorsList.push('Grade: To list on CollX, please select a Grade from available options')
      }
    }

    if (grader && !card.grade_number) {
      errorsList.push('Grade: To list on CollX, please select a Grade from available options')
    }
  }

  if (card.graded && !card.grader) {
    errorsList.push('Grader: To list on CollX, please select a Grader from available options')
  }

  if (!card.graded && card.condition) {
    if (cardStore.selectedCardData.type === 'tcg' && !TCG_CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }

    if (cardStore.selectedCardData.type === 'sports' && !CONDITION.find(item => item.name === card.condition)) {
      errorsList.push('Condition: To list on CollX, please select Condition from available options')
    }
  }

  if (!card.graded && !card.condition) {
    errorsList.push('Condition: To list on CollX, please select Condition from available options')
  }

  return errorsList
}

export const preparedListOfCardsToExportOrPublish = ({ items, selectedCards = {}, isBulkEditing, selectedRanges = [] }) => {
  let items_ = [...items]

  if (isBulkEditing) {
    if (Object.keys(selectedCards).length && !selectedRanges.length) {
      items_ = Object.entries(selectedCards).map(([l, value]) => value)
    } else if (selectedRanges && selectedRanges.length) {
      items_ = selectedRanges.reduce((acc, item) => {
        const cardData = items_.find(card => card.id === item.id)

        if (cardData) acc.push(cardData)

        return acc
      }, [])
    }
  }

  const cardsIds = items_.reduce((acc, item) => {
    acc.push(item.id)

    return acc
  }, [])

  return { cardsIds, cards: items_ }
}

export const dateScheduledTemplateErrors = (connectionStore) => {
  const errors = []
  let dateScheduledTemplateError = false

  if (connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.schedule_date) {
    const scheduleDate = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_date
    const scheduleTime = connectionStore.selectedTemplatePerConnection['ebay-direct'].schedule_time
    
    // Combine date and time
    const scheduledDateTime = new Date(`${scheduleDate} ${scheduleTime}`)
    const now = new Date()
    
    // Check if scheduled date/time is in the past
    if (scheduledDateTime <= now && !connectionStore.selectedTemplatePerConnection['ebay-direct'].make_live_immediately) {
      errors.push('Please update your template to use a future start date, or set the listing to go live immediately.')
      dateScheduledTemplateError = true
    }
  }

  return { errors, dateScheduledTemplateError }
}

export const hasEbayPoliciesTemplateErrors = (connectionStore) => {
  return !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_location_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_fulfillment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_payment_policy_id 
  || !connectionStore.selectedTemplatePerConnection?.['ebay-direct']?.ebay_return_policy_id
}

export const validateIsCardsHasCategoryNotEqualToTemplate = (card, connectionStore) => {
  const cardStore = useCardStore()
  let isCardsWithoutCategoryBasedOnTemplate = false
  let isCardsHasCategoryNotEqualToTemplate = false

  if (card.type !== EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = true
  }

  if (isCardsHasCategoryNotEqualToTemplate && card.type === EBAY_TYPES_BY_CATEGORIES[connectionStore.selectedTemplatePerConnection?.[cardStore.isTryToExport ? 'ebay' : 'ebay-direct']?.ebay_category]) {
    isCardsHasCategoryNotEqualToTemplate = false
    isCardsWithoutCategoryBasedOnTemplate = true
  }

  return { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate }
}

export const checkAreRequiredFieldsEmpty = (cards, connections) => {
  if (!connections.length) return

  const connectionStore = useConnectionStore()
  const cardStore = useCardStore()

  let isCollxError = false
  let isEbayError = false
  let isWhatnotError = false
  let isEbayTemplateError = false
  let isShopifyError = false
  let isDateScheduledTemplateError = false
  let cardsWithoutCategoryBasedOnTemplate = false
  let cardsHasCategoryNotEqualToTemplate = false

  const selectedCardsValues = Object.keys(cardStore.selectedCards).length
    ? Object.values(cardStore.selectedCards)
    : cards

  const isNotListable = (card) =>
    (typeof card.quantity === 'number' && card.quantity <= 0) ||
    card.status === 'sold_out' ||
    card.inventoryStatus === 'sold_out'

  const zeroQtyCards = selectedCardsValues.filter(isNotListable)
  const zeroQuantityAll = zeroQtyCards.length === selectedCardsValues.length && selectedCardsValues.length > 0
  const zeroQuantityPartial = !zeroQuantityAll && zeroQtyCards.length > 0

  // any selected item with a non-alphanumeric SKU is a blocking
  // error, handled like the other listing errors: it disables the Publish/List
  // button and the per-item reason is surfaced in the error modal.
  const skuInvalid = selectedCardsValues.some(card => !isValidSku(card.sku))

  for(let i = 0; i < cards.length; i++) {
    const errors = getErrorsForListToCollX(cards[i], true)

    if (!isEbayTemplateError) {
      isEbayTemplateError = hasEbayPoliciesTemplateErrors(connectionStore)
    }

    if (errors.length) {
      isCollxError = true
    }

    if (cards[i].has_errors) {
      isEbayError = true
      isWhatnotError = true
      isShopifyError = true
    }

    const { dateScheduledTemplateError } = dateScheduledTemplateErrors(connectionStore)
    const { isCardsWithoutCategoryBasedOnTemplate, isCardsHasCategoryNotEqualToTemplate } = validateIsCardsHasCategoryNotEqualToTemplate(cards[i], connectionStore)

    if (isCardsWithoutCategoryBasedOnTemplate) {
      cardsWithoutCategoryBasedOnTemplate = true
    }

    if (isCardsHasCategoryNotEqualToTemplate) {
      cardsHasCategoryNotEqualToTemplate = true
    }

    if (dateScheduledTemplateError) {
      isDateScheduledTemplateError = true
    }

    if (isCollxError && isEbayError) break
  }

  return {
    collx: isCollxError,
    ebay: isEbayError,
    whatnot: isWhatnotError,
    ebayTemplate: isEbayTemplateError,
    shopify: isShopifyError,
    dateScheduledTemplateError: isDateScheduledTemplateError,
    cardsWithoutCategoryBasedOnTemplate,
    cardsHasCategoryNotEqualToTemplate,
    zeroQuantityAll,
    zeroQuantityPartial,
    skuInvalid,
  }
}

export const getViewGraderWebsiteLookupLink = (grader, certNo) => {
  return `${VIEW_GRADER_WEBSITE_LOOKUP_LINKS[grader]}${certNo}`
}

 // Function to check if a cell contains a custom component that needs manual focus handling
 export const  isCustomComponentCell = (cellPosition, params) => {
  const column = cellPosition.column
  const rowNode = params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)
  
  // Check based on column ID, field, or other properties identifying custom components
  return column.getColId() === 'subset' || column.getColId() === 'attributes'  // Update this ID as needed
}

// Function to manually focus the custom component in the cell
export const focusCustomComponent = (cellPosition, params) => {
  const cell = params.api.getCellRendererInstances({
    rowNodes: [params.api.getDisplayedRowAtIndex(cellPosition.rowIndex)],
    columns: [cellPosition.column],
  })[0]


  if (cell && cellPosition.column.colId === 'subset') {
    const element = cell.params?.eParentOfValue
    const inputElements = element.getElementsByTagName('input')
    const input = inputElements && inputElements[0]
    
    if (element && (typeof element.focus === 'function' || typeof input.focus === 'function')) {
      input ? input.focus() : element.focus() // Set focus to the custom component's element
    }

    if (element && (typeof element.click === 'function' || typeof input.click === 'function')) {
      input ? input.click() : element.click() // Set focus to the custom component's element

      if (!input) {
        const clickableElements = element.getElementsByTagName('section')
        const clickableEl = clickableElements[1]

        clickableEl.click()
      }
      
    } else {
      // If `click()` isn't directly available, use a custom click event
      const clickEvent = new MouseEvent('click', { bubbles: true, cancelable: true })
      element.dispatchEvent(clickEvent)
    }
  // } else if (cell && cellPosition.column.colId === 'attributes') {
  //   const element = cell.params?.eParentOfValue
  //   console.log('!!!!!!!!!!!! element', cell.params, element)

  //   if (element && typeof element.click === 'function') {
  //     const clickableElements = element.getElementsByTagName('section')
  //     const clickableEl = clickableElements[0]
  //     console.log('>> click', element)

  //     clickableEl.click()
      
  //   }
  // }
  }
}

export const getDisplayColumns = (key, userId) => {
  const savedColumns = localStorage.getItem(key)
  if (savedColumns) {
    const parsedColumns = JSON.parse(savedColumns)
    return parsedColumns[userId]
  }

  return []
}

export const getSelectedId = (selected) => {
  if (!selected) return null
  return selected.id || selected.collx_type_id || selected.type || null
}

export const valueSectionStyleForBulkInputs = ({selectedAttribute, valueState, fields}) => {
  let paddingLeft = 0
  if (
    !['status', 'start_price', 'category', 'sport', 'year', 'attributes', 'graded', 'grader', 'grade', 'purchase_price', 'condition'].includes(selectedAttribute?.field)
  ) {
    if (
      (fields[selectedAttribute.field]
      && valueState === 'entered')
      || selectedAttribute.field === 'title'
      || selectedAttribute.field === 'description'
    ) {
      paddingLeft = 17
    }
  }

  if (
    selectedAttribute.field === 'year'
    && fields?.year
    && valueState === 'entered'
  ) {
    paddingLeft = 8
  }

  if (
    selectedAttribute.field === 'attributes'
    && valueState
  ) {
    paddingLeft = 10
  }

  return {
    'padding-left': paddingLeft + 'px'
  }
}

export const getPreparedOptions = (options) => {
  return options.reduce((acc, option) => {
    if (!option.collx_set_id ) {
      acc.push({
        batch_id: null,
        collx_set_id: option.id,
        collx_set_name: option.name,
        include_parallels: option.lock_includes_parallels === undefined ? option.is_base : option.lock_includes_parallels,
        is_base: option.is_base,
      })
    } else {
      acc.push(option)
    }

    return acc
  }, [])
}